<div class="invitations-table-container">
  <div class="page-header">
    <h1>Invitations</h1>
    <p-button label="Refresh" icon="pi pi-refresh" severity="primary" [text]="true" (onClick)="onRefresh()"></p-button>
  </div>

  <div class="filter-panel">
    <p-iconfield iconPosition="left">
      <p-inputicon>
        <i class="pi pi-search"></i>
      </p-inputicon>
      <input
        type="text"
        pInputText
        placeholder="Search email, name..."
        [value]="searchTerm()"
        (input)="onSearch($event)"
      />
    </p-iconfield>

    <p-select
      class="filter-select"
      optionLabel="label"
      optionValue="value"
      placeholder="Status"
      [options]="statusOptions()"
      [ngModel]="statusFilter()"
      (ngModelChange)="onStatusFilterChange($event)"
    ></p-select>

    <p-select
      class="filter-select"
      optionLabel="label"
      optionValue="value"
      placeholder="Household Relationship"
      [options]="householdRelationshipOptions()"
      [ngModel]="householdRelationshipFilter()"
      (ngModelChange)="onHouseholdRelationshipFilterChange($event)"
    ></p-select>

    <input
      type="text"
      pInputText
      class="filter-text"
      placeholder="Filter by group"
      [value]="groupFilter()"
      (input)="onGroupFilterChange($event)"
    />

    <input
      type="text"
      pInputText
      class="filter-text"
      placeholder="Filter by inviter"
      [value]="inviterFilter()"
      (input)="onInviterFilterChange($event)"
    />

    <div class="filter-actions">
      <p-button
        label="Clear"
        icon="pi pi-times"
        severity="secondary"
        [text]="true"
        (onClick)="onClearFilters()"
      ></p-button>
    </div>
  </div>

  @if (invitationsResource.error()) {
    <div class="load-error">
      <i class="pi pi-exclamation-triangle"></i>
      <span>Failed to load invitations. The list below is not the full picture.</span>
      <p-button label="Retry" severity="secondary" [text]="true" (onClick)="onRefresh()"></p-button>
    </div>
  }

  <p-table
    [scrollable]="true"
    [value]="filteredInvitations()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-invitation>
      <tr>
        <td>
          <strong>{{ invitation.inviteeEmail }}</strong>
        </td>
        <td>
          <span>{{ invitation.inviterName || invitation.inviterUserId }}</span>
        </td>
        <td>
          <span>{{ invitation.groupName || invitation.groupId }}</span>
        </td>
        <td>
          <p-tag [value]="invitation.status" [severity]="getStatusSeverity(invitation.status)"></p-tag>
        </td>
        <td>
          <p-tag
            [value]="invitation.householdRelationship"
            [severity]="getHouseholdRelationshipSeverity(invitation.householdRelationship)"
          ></p-tag>
        </td>
        <td>{{ formatDate(invitation.expiresOn) }}</td>
        <td>{{ formatDate(invitation.createdOn) }}</td>
        <td>
          <p-button
            icon="pi pi-eye"
            severity="info"
            pTooltip="View details"
            tooltipPosition="top"
            [text]="true"
            [rounded]="true"
            (onClick)="onViewInvitation(invitation)"
          ></p-button>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            @if (invitationsResource.error()) {
              <i class="pi pi-exclamation-triangle large-icon"></i>
              <p>Invitations could not be loaded</p>
              <small>The request failed, so this is not an empty list. Retry above.</small>
            } @else {
              <i class="pi pi-envelope large-icon"></i>
              <p>No invitations found</p>
              @if (
                searchTerm() || statusFilter() || householdRelationshipFilter() || groupFilter() || inviterFilter()
              ) {
                <small>Try adjusting your filters</small>
              } @else {
                <small>Invitations will appear here once they are staged or sent</small>
              }
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  header="Invitation Details"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDetailDialog"
>
  @if (selectedInvitation(); as inv) {
    <div class="invitation-details">
      <div class="detail-row">
        <span class="detail-label">Invitation ID</span>
        <span class="detail-value monospace">{{ inv.invitationId }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Status</span>
        <span class="detail-value">
          <p-tag [value]="inv.status" [severity]="getStatusSeverity(inv.status)"></p-tag>
        </span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Household Relationship</span>
        <span class="detail-value">
          <p-tag
            [value]="inv.householdRelationship"
            [severity]="getHouseholdRelationshipSeverity(inv.householdRelationship)"
          ></p-tag>
        </span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Invitee Email</span>
        <span class="detail-value">{{ inv.inviteeEmail }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Invitee Name</span>
        <span class="detail-value">
          @if (inv.inviteeFirstName || inv.inviteeLastName) {
            {{ inv.inviteeFirstName || '' }} {{ inv.inviteeLastName || '' }}
          } @else {
            <span class="muted">N/A</span>
          }
        </span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Invitee Date of Birth</span>
        <span class="detail-value">{{ inv.inviteeDateOfBirth || 'N/A' }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Invitee User ID</span>
        <span class="detail-value monospace">{{ inv.inviteeUserId || 'Not yet linked' }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Inviter</span>
        <span class="detail-value">{{ inv.inviterName || inv.inviterUserId }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Group</span>
        <span class="detail-value">{{ inv.groupName || inv.groupId }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Relationship</span>
        <span class="detail-value">{{ inv.relationship || 'N/A' }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Has Smart Device</span>
        <span class="detail-value">{{ formatBool(inv.hasSmartDevice) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Share Inviter Information</span>
        <span class="detail-value">{{ formatBool(inv.shareInviterInformation) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Created</span>
        <span class="detail-value">{{ formatDateTime(inv.createdOn) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Expires</span>
        <span class="detail-value">{{ formatDateTime(inv.expiresOn) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Accepted</span>
        <span class="detail-value">{{ formatDateTime(inv.acceptedOn) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Declined</span>
        <span class="detail-value">{{ formatDateTime(inv.declinedOn) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Cancelled</span>
        <span class="detail-value">{{ formatDateTime(inv.cancelledOn) }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Token</span>
        <span class="detail-value monospace token">{{ inv.invitationToken }}</span>
      </div>
    </div>
  }

  <ng-template pTemplate="footer">
    <p-button
      label="Update Email"
      icon="pi pi-pencil"
      severity="info"
      [text]="true"
      [disabled]="!canCancelOrEditEmail() || processingAction()"
      (onClick)="onOpenUpdateEmail()"
    ></p-button>
    <p-button
      label="Resend"
      icon="pi pi-send"
      severity="primary"
      [text]="true"
      [disabled]="!canResend() || processingAction()"
      [loading]="processingAction()"
      (onClick)="onResend()"
    ></p-button>
    <p-button
      label="Cancel Invitation"
      icon="pi pi-ban"
      severity="danger"
      [text]="true"
      [disabled]="!canCancelOrEditEmail() || processingAction()"
      (onClick)="onCancelInvitation()"
    ></p-button>
    <p-button label="Close" severity="secondary" (onClick)="showDetailDialog.set(false)"></p-button>
  </ng-template>
</p-dialog>

<p-dialog
  header="Update Invitation Email"
  [modal]="true"
  [style]="{ width: '30rem' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showUpdateEmailDialog"
>
  <div class="update-email-form">
    <label for="newEmail">New email address</label>
    <input id="newEmail" type="email" pInputText [value]="newEmail()" (input)="onUpdateEmailInput($event)" />
    @if (newEmail() && !isEmailValid()) {
      <small class="muted">Enter a valid email address.</small>
    }
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" (onClick)="showUpdateEmailDialog.set(false)"></p-button>
    <p-button
      label="Save"
      icon="pi pi-check"
      [disabled]="!isEmailValid() || processingAction()"
      [loading]="processingAction()"
      (onClick)="onSaveNewEmail()"
    ></p-button>
  </ng-template>
</p-dialog>
